import { useEffect, useState } from 'react';
import ColorThief from 'colorthief';
import { isEqual } from 'lodash';
import { appComponentsStorage } from '../../storage/appComponentsStorage';

let colorThief;

/**
 * Handles building an image component and calling a callback function after the image loads
 * @param src image url
 * @param onComplete callback function called on load && complete of the image
 */

type InitializeImageCallbacks = {
  onComplete: (imageCompoennt: any) => void;
  onError: () => void;
};

const initializeImage = (
  src: string,
  { onComplete, onError }: InitializeImageCallbacks
) => {
  const imageComponent = new Image();
  imageComponent.alt = 'image';
  imageComponent.crossOrigin = 'anonymous';
  imageComponent.width = 10;
  imageComponent.height = 10;

  imageComponent.onload = () => {
    if (imageComponent.complete) {
      onComplete(imageComponent);
    }
    // clears the binding.
    imageComponent.onload = null;
  };

  imageComponent.onerror = () => {
    onError();
  };

  imageComponent.src = src.startsWith('blob:') ? src : `${src}&cors=true`;
};

export type ColorfulColor = {
  colorfulColor: Color;
  colorfulnessIndex: number;
};

/**
 * Gets the most colorful color from an image, using ColorThief
 * @param img the image HTML component
 */
const getColorfulColor = (img: HTMLImageElement): ColorfulColor => {
  if (!ColorThief) return null;

  if (!colorThief) {
    colorThief = new ColorThief();
  }

  const colorCount = 8;
  const colorQuality = 10;

  /**
   * @todo (WEB-1490): try…catch here in order to avoid a non-fatal but pesky error
   * inside the call to `getPalette`. this should only be a temporary solution to
   * minimise console noise pending color-thief's replacement.
   */
  let palette;

  try {
    palette = colorThief.getPalette(img, colorCount, colorQuality);
  } catch {
    if (!palette) {
      return {
        colorfulColor: undefined,
        colorfulnessIndex: undefined,
      };
    }
  }

  let currentColorfulness = 0;
  let currentColorfulColor: Color = palette[0];

  // Loops through the palette and finds most colorful color
  palette.forEach((color: Color) => {
    let tempColorfulness = currentColorfulness;

    // (0,0,0) will return NAN if used in the formula, if (0,0,0) leave colorfulness as its default 0
    if (!isEqual(color, [0, 0, 0])) {
      const [red, green, blue] = color;
      const min = Math.min(red, green, blue);
      const max = Math.max(red, green, blue);

      // calculate colorfulness of color
      tempColorfulness = ((max + min) * (max - min)) / max;
    }

    // compare color's colorfulness against previous colorfulness to determine if color is "colorful" enough
    if (tempColorfulness > currentColorfulness) {
      currentColorfulness = tempColorfulness;
      currentColorfulColor = color;
    }
  });

  return {
    colorfulColor: currentColorfulColor,
    colorfulnessIndex: currentColorfulness,
  };
};

/**
 * Gets cached color info from localStorage
 * @param imgUrl image url
 */
const getCachedColorInfo = ({ imgUrl }: { imgUrl: string }) => {
  const cacheValue =
    appComponentsStorage.get('color-cache', {})[imgUrl] ?? null;
  if (cacheValue?.colorfulColor && cacheValue?.colorfulnessIndex)
    return cacheValue;
  return null;
};
/**
 * Sets cached color info from localStorage
 * @param imgUrl image url
 */
const setCachedColorInfo = ({
  imgUrl,
  color,
}: {
  imgUrl: string;
  color: ColorfulColor;
}) =>
  appComponentsStorage.set('color-cache', (draft) => {
    draft[imgUrl] = color;
    return draft;
  });

const getCacheKey = (url) => {
  const isAbsoluteUrl = RegExp('^(?:[a-z]+:)?//', 'i').test(url);
  const isBlobUrl = /^blob:/i.test(url);

  if (isAbsoluteUrl || isBlobUrl) {
    try {
      const safeUrl = new URL(url);
      safeUrl.searchParams.delete('token');
      return safeUrl.toString();
    } catch (e) {
      return null;
    }
  }

  return null;
};

type ColorThiefProps = {
  imgUrl?: string;
};

export type Color = [number, number, number];

type ColorThiefResult = {
  color?: Color;
  isLoading: boolean;
  colorfulness: number;
};

export const useColorThief = ({
  imgUrl,
}: ColorThiefProps): ColorThiefResult => {
  const [color, setColor] = useState(null);
  const [colorfulness, setColorfulness] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const imageCacheKey = getCacheKey(imgUrl);

  /** When a new imgUrl is passed in, need to re-initilize the image and re-calculate the palette */
  useEffect(() => {
    // return early if no URL
    if (!imgUrl) {
      setIsLoading(false);
      setColor(null);
      setColorfulness(0);
      return;
    }

    // check the cache for color info for this URL
    const cachedColor = getCachedColorInfo({ imgUrl: imageCacheKey });

    if (cachedColor) {
      setIsLoading(false);
      setColor(cachedColor.colorfulColor);
      setColorfulness(cachedColor.colorfulnessIndex);
    }

    // compute color from image
    else {
      setIsLoading(true);

      initializeImage(imgUrl, {
        onComplete: (imageComponent) => {
          const calculatedColor = getColorfulColor(imageComponent);
          setColor(calculatedColor.colorfulColor);
          setColorfulness(calculatedColor.colorfulnessIndex);
          setCachedColorInfo({
            imgUrl: imageCacheKey,
            color: calculatedColor,
          });
          setIsLoading(false);
        },
        onError: () => {
          setIsLoading(false);
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageCacheKey]);

  return {
    color,
    isLoading,
    colorfulness,
  };
};
